import { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import {
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

interface SecuritiesSubItem {
  title: string;
  url: string;
  slug: string;
}

const securitiesSubItems: SecuritiesSubItem[] = [
  { title: 'Overview', url: '/investments/securities', slug: 'overview' },
  { title: 'Equity', url: '/investments/securities/equity', slug: 'equity' },
  { title: 'Debt Securities', url: '/investments/securities/debt-securities', slug: 'debt-securities' },
  { title: 'Derivatives', url: '/investments/securities/derivatives', slug: 'derivatives' },
  { title: 'ETFs', url: '/investments/securities/etfs', slug: 'etfs' },
  { title: 'Index Funds', url: '/investments/securities/index-funds', slug: 'index-funds' },
];

const STORAGE_KEY = 'sidebar:securities-expanded';

function getStoredSecuritiesExpansionState(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : false;
  } catch {
    return false;
  }
}

function setStoredSecuritiesExpansionState(expanded: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(expanded));
  } catch {
    // ignore
  }
}

export const SecuritiesMenuItem: React.FC = () => {
  const [expanded, setExpanded] = useState<boolean>(() => getStoredSecuritiesExpansionState());
  const location = useLocation();
  const isSecuritiesActive = location.pathname.startsWith('/investments/securities');
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const subItemsRef = useRef<(HTMLAnchorElement | null)[]>([]);

  const handleToggle = useCallback(() => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    setStoredSecuritiesExpansionState(newExpanded);
  }, [expanded]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          handleToggle();
          break;
        case 'ArrowDown':
          if (expanded) {
            event.preventDefault();
            subItemsRef.current[0]?.focus();
          }
          break;
        case 'Escape':
          if (expanded) {
            event.preventDefault();
            setExpanded(false);
            setStoredSecuritiesExpansionState(false);
            toggleButtonRef.current?.focus();
          }
          break;
      }
    },
    [expanded, handleToggle]
  );

  const handleSubItemKeyDown = useCallback(
    (index: number, event: React.KeyboardEvent<HTMLAnchorElement>) => {
      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          const nextIndex = (index + 1) % securitiesSubItems.length;
          subItemsRef.current[nextIndex]?.focus();
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const prevIndex = index === 0 ? securitiesSubItems.length - 1 : index - 1;
          subItemsRef.current[prevIndex]?.focus();
          break;
        }
        case 'Escape':
          event.preventDefault();
          setExpanded(false);
          setStoredSecuritiesExpansionState(false);
          toggleButtonRef.current?.focus();
          break;
      }
    },
    []
  );

  useEffect(() => {
    if (!expanded) {
      subItemsRef.current = [];
    }
  }, [expanded]);

  return (
    <>
      <SidebarMenuSubItem role="none">
        <button
          ref={toggleButtonRef}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          aria-expanded={expanded}
          aria-controls="securities-submenu"
          aria-haspopup="true"
          aria-label={`Securities section, ${expanded ? 'expanded' : 'collapsed'}. Press Enter or Space to ${expanded ? 'collapse' : 'expand'}.`}
          className={cn(
            'flex w-full items-center gap-2 overflow-hidden rounded-md px-2 py-1 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding]',
            'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            'focus-visible:ring-2 focus:outline-none focus:ring-2 focus:ring-sidebar-ring focus:ring-offset-1 focus:ring-offset-sidebar focus:z-10',
            isSecuritiesActive && 'bg-sidebar-accent font-medium text-sidebar-accent-foreground',
          )}
        >
          <span className="flex-1 truncate">Securities</span>
          {expanded ? (
            <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0" aria-hidden="true" />
          )}
        </button>
      </SidebarMenuSubItem>

      {expanded && (
        <div
          id="securities-submenu"
          role="group"
          aria-label="Securities types"
          className="ml-3 border-l border-sidebar-border pl-2"
        >
          {securitiesSubItems.map((item, index) => {
            const isActive = location.pathname === item.url;

            return (
              <SidebarMenuSubItem key={item.slug} role="none">
                <SidebarMenuSubButton
                  asChild
                  isActive={isActive}
                  role="menuitem"
                  aria-current={isActive ? 'page' : undefined}
                  aria-posinset={index + 1}
                  aria-setsize={securitiesSubItems.length}
                  className="focus:outline-none focus:ring-2 focus:ring-sidebar-ring focus:ring-offset-1 focus:ring-offset-sidebar focus:z-10"
                >
                  <NavLink
                    to={item.url}
                    ref={(el) => {
                      subItemsRef.current[index] = el;
                    }}
                    onKeyDown={(e) => handleSubItemKeyDown(index, e)}
                    aria-label={`${item.title} securities`}
                    tabIndex={0}
                  >
                    <span>{item.title}</span>
                  </NavLink>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            );
          })}
        </div>
      )}
    </>
  );
};
